/* BSP30 (Half-Life) lesen: Lumps, Geometrie, Texinfo, Lightmaps, Modelle und eingebettete Texturen. Keine DOM-Abhängigkeit. */
import type { Vec3 } from '../model/types.ts';
import I18n from '../i18n.ts';

const _ = I18n.t;

export const LUMP = { ENT: 0, PLANES: 1, TEX: 2, VERTS: 3, VIS: 4, NODES: 5, TEXINFO: 6, FACES: 7, LIGHT: 8, CLIP: 9, LEAFS: 10, MARK: 11, EDGES: 12, SURF: 13, MODELS: 14 } as const;
export const NLUMPS = 15;

export interface BspLump { ofs: number; len: number }
/** Texturachsen einer Fläche: s/t-Richtung, Verschiebung, Texturindex und Flags (1 = ohne Lightmap, z. B. Himmel/Wasser) */
export interface BspTexInfo { s: Vec3; so: number; t: Vec3; to: number; tex: number; flags: number }
/** nz: Z-Anteil der Flächennormalen auf der sichtbaren Seite (zeigt in den Raum, bei Decken nach unten) */
export interface BspFace { first: number; num: number; info: number; style: number; light: number; nz: number }
export interface BspModel { min: Vec3; max: Vec3; origin: Vec3; first: number; num: number }
/** Texturname und Größe; rgba nur, wenn die Pixel in der BSP eingebettet sind */
export interface BspTexture { name: string; w: number; h: number; rgba: Uint8ClampedArray | null }

export interface BspFile {
  version: number;
  lumps: BspLump[];
  buf: ArrayBuffer;
  entText: string;
  verts: Float32Array;
  edges: Uint16Array;
  surf: Int32Array;
  texinfo: BspTexInfo[];
  faces: BspFace[];
  lighting: Uint8Array;
  models: BspModel[];
  textures: BspTexture[];
}

const vec3 = (f: number[], o: number): Vec3 => [f[o], f[o + 1], f[o + 2]];

export function parse(buf: ArrayBuffer): BspFile {
  const dv = new DataView(buf), u8 = new Uint8Array(buf);
  if (buf.byteLength < 4 + NLUMPS * 8) throw new Error(_('bsp.e_short'));
  const ver = dv.getInt32(0, true);
  if (ver !== 30) throw new Error(_('bsp.e_version', { ver: ver }));
  const lumps: BspLump[] = [];
  for (let i = 0; i < NLUMPS; i++) {
    const o = dv.getInt32(4 + i * 8, true), l = dv.getInt32(8 + i * 8, true);
    if (o < 0 || l < 0 || o + l > buf.byteLength) throw new Error(_('bsp.e_lump', { i: i }));
    lumps.push({ ofs: o, len: l });
  }
  const L = (i: number): BspLump => lumps[i];
  // Entities
  let n = L(LUMP.ENT).len; const eo = L(LUMP.ENT).ofs;
  while (n > 0 && u8[eo + n - 1] === 0) n--;
  const entText = new TextDecoder('latin1').decode(u8.subarray(eo, eo + n));
  // Vertices
  const nv = Math.floor(L(LUMP.VERTS).len / 12), vo = L(LUMP.VERTS).ofs;
  const verts = new Float32Array(nv * 3);
  for (let i = 0; i < nv * 3; i++) verts[i] = dv.getFloat32(vo + i * 4, true);
  // Edges / Surfedges
  const ne = Math.floor(L(LUMP.EDGES).len / 4), eo2 = L(LUMP.EDGES).ofs;
  const edges = new Uint16Array(ne * 2);
  for (let i = 0; i < ne * 2; i++) edges[i] = dv.getUint16(eo2 + i * 2, true);
  const ns = Math.floor(L(LUMP.SURF).len / 4), so = L(LUMP.SURF).ofs;
  const surf = new Int32Array(ns);
  for (let i = 0; i < ns; i++) surf[i] = dv.getInt32(so + i * 4, true);
  // Texinfo
  const nt = Math.floor(L(LUMP.TEXINFO).len / 40), to = L(LUMP.TEXINFO).ofs;
  const texinfo: BspTexInfo[] = [];
  for (let i = 0; i < nt; i++) {
    const o = to + i * 40, v: number[] = [];
    for (let k = 0; k < 8; k++) v.push(dv.getFloat32(o + k * 4, true));
    texinfo.push({ s: vec3(v, 0), so: v[3], t: vec3(v, 4), to: v[7], tex: dv.getUint32(o + 32, true), flags: dv.getUint32(o + 36, true) });
  }
  // Faces
  const nf = Math.floor(L(LUMP.FACES).len / 20), fo = L(LUMP.FACES).ofs;
  const faces: BspFace[] = [];
  const npl = Math.floor(L(LUMP.PLANES).len / 20), plo = L(LUMP.PLANES).ofs;
  for (let i = 0; i < nf; i++) {
    const o = fo + i * 20;
    const pi = dv.getUint16(o, true), side = dv.getUint16(o + 2, true);
    const nz = pi < npl ? (side ? -1 : 1) * dv.getFloat32(plo + pi * 20 + 8, true) : 0;
    faces.push({ nz: nz, first: dv.getInt32(o + 4, true), num: dv.getUint16(o + 8, true), info: dv.getUint16(o + 10, true), style: u8[o + 12], light: dv.getInt32(o + 16, true) });
  }
  // Lighting
  const lighting = u8.subarray(L(LUMP.LIGHT).ofs, L(LUMP.LIGHT).ofs + L(LUMP.LIGHT).len);
  // Models
  const nm = Math.floor(L(LUMP.MODELS).len / 64), mo = L(LUMP.MODELS).ofs;
  const models: BspModel[] = [];
  for (let i = 0; i < nm; i++) {
    const o = mo + i * 64, f: number[] = [];
    for (let k = 0; k < 9; k++) f.push(dv.getFloat32(o + k * 4, true));
    models.push({ min: vec3(f, 0), max: vec3(f, 3), origin: vec3(f, 6), first: dv.getInt32(o + 56, true), num: dv.getInt32(o + 60, true) });
  }
  // Texturen (eingebettete Pixel, falls vorhanden)
  const textures: BspTexture[] = [];
  const xo = L(LUMP.TEX).ofs;
  if (L(LUMP.TEX).len >= 4) {
    const cnt = dv.getInt32(xo, true);
    for (let i = 0; i < cnt; i++) {
      const off = dv.getInt32(xo + 4 + i * 4, true);
      if (off < 0 || xo + off + 40 > buf.byteLength) { textures.push({ name: '', w: 16, h: 16, rgba: null }); continue; }
      const o = xo + off;
      let name = ''; for (let k = 0; k < 16 && u8[o + k]; k++) name += String.fromCharCode(u8[o + k]);
      const w = dv.getUint32(o + 16, true), h = dv.getUint32(o + 20, true), m0 = dv.getUint32(o + 24, true);
      const t: BspTexture = { name: name, w: w, h: h, rgba: null };
      if (m0 > 0 && w > 0 && h > 0 && w <= 4096 && h <= 4096) {
        const sz = w * h + (w >> 1) * (h >> 1) + (w >> 2) * (h >> 2) + (w >> 3) * (h >> 3), po = o + m0 + sz + 2;
        if (po + 768 <= buf.byteLength) {
          const rgba = new Uint8ClampedArray(w * h * 4), px = o + m0, key = name.charAt(0) === '{';
          for (let k = 0; k < w * h; k++) {
            const ix = u8[px + k];
            rgba[k * 4] = u8[po + ix * 3]; rgba[k * 4 + 1] = u8[po + ix * 3 + 1]; rgba[k * 4 + 2] = u8[po + ix * 3 + 2]; rgba[k * 4 + 3] = key && ix === 255 ? 0 : 255;
          }
          t.rgba = rgba;
        }
      }
      textures.push(t);
    }
  }
  return { version: ver, lumps: lumps, buf: buf, entText: entText, verts: verts, edges: edges, surf: surf, texinfo: texinfo, faces: faces, lighting: lighting, models: models, textures: textures };
}

/** Eckpunkte einer Fläche (Weltkoordinaten). */
export function faceVerts(b: BspFile, f: BspFace): Vec3[] {
  const out: Vec3[] = [];
  for (let k = 0; k < f.num; k++) {
    const se = b.surf[f.first + k], v = se >= 0 ? b.edges[se * 2] : b.edges[-se * 2 + 1];
    out.push([b.verts[v * 3], b.verts[v * 3 + 1], b.verts[v * 3 + 2]]);
  }
  return out;
}
